import type {
  StartChatRequest,
  StartChatResponse,
  SendMessageRequest,
  SendMessageResponse,
} from './types';

const CHAT_API_URL = process.env.REACT_APP_CHAT_API_URL ?? '';
const CHAT_API_KEY = process.env.REACT_APP_CHAT_API_KEY ?? '';

export class ChatApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ChatApiError';
  }
}

async function chatFetch<T>(path: string, body: unknown): Promise<T> {
  const url = `${CHAT_API_URL.replace(/\/$/, '')}${path}`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': CHAT_API_KEY },
      body: JSON.stringify(body),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(
      `Cannot reach chat API (${msg}). Check REACT_APP_CHAT_API_URL and that the chat service is running.`,
    );
  }

  const rawText = await response.text();
  let parsed: unknown;
  try {
    parsed = rawText ? JSON.parse(rawText) : {};
  } catch {
    throw new ChatApiError(
      response.status,
      `Chat API returned non-JSON (HTTP ${response.status}). Body: ${rawText.slice(0, 280)}`,
    );
  }

  if (!response.ok) {
    const obj = parsed as { error?: string };
    const serverMsg =
      typeof obj.error === 'string' && obj.error.trim()
        ? obj.error.trim()
        : rawText.slice(0, 400);
    throw new ChatApiError(response.status, serverMsg);
  }

  return parsed as T;
}

export function startChatSession(payload: StartChatRequest): Promise<StartChatResponse> {
  return chatFetch<StartChatResponse>('/chat/start', payload);
}

export function sendChatMessage(payload: SendMessageRequest): Promise<SendMessageResponse> {
  return chatFetch<SendMessageResponse>('/chat/message', payload);
}

export function endChatSession(payload: { session_id: string }): Promise<void> {
  return chatFetch<void>('/chat/end', payload);
}
